import { useHistory } from 'react-router';
import { Edit } from '@material-ui/icons';
import { Box, Flex, IconButton, Popover, PopoverContent, PopoverTrigger } from '@chakra-ui/react';

import { entityBaseBodyRender } from '@app/components/common/table/body-renders/entity-base';
import { MasterOutlet } from '@app/api/models/acb';
import { dateTimeBodyRender } from '@app/components/common/table/body-renders/date-time';
import { TableColumn } from '@app/components/common/table';
import { Txt } from '@app/components/common'; 

import FrequencyVisitsModal from '../../new-outlets/frequency-visits-modal';
import { CommentColumn } from '../components/commet-column';

// eslint-disable-next-line import/no-anonymous-default-export
export const useColumns = ({ canEdit }: { canEdit: boolean }): TableColumn<MasterOutlet>[] => {
  const history = useHistory();
  return [
    {
      name: 'id',
      label: '#',
      options: {
        filter: true,
        sort: true,
        customBodyRender: (id, { rowData }) => {
          return <>
            <Box display="flex">
              <Txt color="brand.500">{id}</Txt>
            </Box>
            {!!rowData.sfa_id && <Box display="flex" fontSize='small'>
              <Txt color="gray.500">ID в SFA:&nbsp;</Txt>
              <Txt>{rowData.sfa_id}</Txt>
            </Box>}
          </>;
        }
      },
    },
    {
      name: 'sfa_id',
      label: 'SFA ID',
      options: {
        filter: true,
        hidden: true,
      },
    },
    {
      name: 'sales_outlet_id',
      label: 'ID ТТ в источнике продаж',
      options: {
        filter: {
          name: 'sales_outlet_id',
          type: 'text',
        },
        sort: false,
        hidden: true,
      },
    },
    {
      name: 'name',
      label: 'Наименование',
      options: {
        filter: true,
        sort: true,
        customBodyRender: (name: string, { rowData }) => (
          <>
            <Txt color="brand.500" style={{ fontSize: 16 }}>
              {name || rowData.legal_name}
            </Txt>
            <Box display="flex">
              <Txt color="gray.500">Юр. наим.:&nbsp;</Txt>
              <Txt color="primary">{rowData.legal_name}</Txt>
            </Box>
            {rowData.title && <Box display="flex">
              <Txt color="gray.500">Вывеска:&nbsp;</Txt>
              <Txt>{rowData.title}</Txt>
            </Box>}
            {rowData.subnet && <Box display="flex">
              <Txt color="gray.500">Подсеть:&nbsp;</Txt>
              <Txt>{rowData.subnet}</Txt>
            </Box>}
          </>
        ),
      },
    },
    {
      name: 'room_type',
      label: 'Тип',
      options: {
        filter: {
          name: 'room_type_id',
          sourceName: 'outlet_room_type',
          type: 'select',
        },
        sort: true,
        customBodyRender: (value: EntityBase) => {
          return value?.name;
        },
      },
    },
    {
      name: 'inn',
      label: 'ИНН',
      options: {
        filter: true,
        sort: true,
      },
    },
    {
      name: 'division',
      label: 'Дивизион',
      options: {
        filter: {
          name: 'division_id',
          sourceName: 'division',
          type: 'multi-select'
        },
        sort: true,
        customBodyRender: entityBaseBodyRender(true),
      },
    },
    {
      name: 'distributor',
      label: 'Дистрибьютор',
      options: {
        filter: {
          name: 'distributor_id',
          sourceName: 'distributor',
          type: 'autocomplete'
        },
        sort: true,
        customBodyRender: entityBaseBodyRender(true),
      },
    },
    {
      name: 'address',
      label: 'Адрес',
      options: {
        filter: false,
        sort: false,
        customBodyRender: (address, { rowData }) => (
          <>
            <Txt wordBreak='normal' minW={250} whiteSpace='break-spaces'>
              {address?.full_address || rowData.address?.address}
            </Txt>
          </>
        ),
      },
    },
    {
      name: 'route_numbers',
      label: 'Маршруты',
      options: {
        filter: {
          name: 'route_number_id',
          sourceName: 'route_number',
          type: 'autocomplete-paginated',
        },
        sort: false,
      },
    },
    {
      name: 'visits_day',
      label: 'Частота посещений в мес.',
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value) => (
          <>
            <FrequencyVisitsModal value={value} />
          </>
        ),
      },
    },
    {
      name: 'comment',
      label: 'Комментарий',
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value) => <CommentColumn text={value} />,
      },
    },
    {
      name: 'segment',
      label: 'Сегмент',
      options: {
        filter: {
          name: 'segment_id',
          sourceName: 'outlet_segment',
          type: 'multi-select'
        },
        sort: true,
        customBodyRender: entityBaseBodyRender(),
      },
    },
    {
      name: 'create_dt',
      label: 'Дата создания',
      options: {
        filter: {
          name: 'create_date_range',
          type: 'date-range',
          onApply: (value) => value ? [value[0], value[1]] : undefined
        },
        sort: true,
        customBodyRender: dateTimeBodyRender(false, true),
      },
    },
    {
      name: 'open_date',
      label: 'Дата открытия',
      options: {
        filter: {
          name: 'open_date_range',
          type: 'date-range',
          onApply: (value) => value ? [value[0], value[1]] : undefined
        },
        sort: true,
        customBodyRender: dateTimeBodyRender(false, true),
      },
    },
    {
      name: 'close_date',
      label: 'Дата закрытия',
      options: {
        filter: {
          name: 'close_date_range',
          type: 'date-range',
          onApply: (value) => value ? [value[0], value[1]] : undefined
        },
        sort: true,
        customBodyRender: dateTimeBodyRender(false, true),
      },
    },
    {
      name: 'status',
      label: 'Статус',
      options: {
        filter: {
          name: 'status_id',
          sourceName: 'outlet_status',
          type: 'select',
          onApply: (value) => value ? [value.id] : undefined
        },
        sort: true,
        customBodyRender: entityBaseBodyRender(false),
      },
    },
    {
      name: 'acb_user',
      label: 'Пользователи',
      options: {
        filter: false,
        sort: false,
        customBodyRender: (_, { rowData }) => {
          return <Flex gap={2} flexDirection="column">
            <Box display="flex" alignItems="center" gap={2}>
              <Txt color="gray.500">Инициатор</Txt>
              <Popover
                placement='bottom'
                closeOnBlur={false}
                trigger='hover'
              >
                <PopoverTrigger>
                  <Txt color="brand.500">{rowData.acb_user?.full_name || '-'}</Txt>
                </PopoverTrigger>
                <PopoverContent>
                  <Box p={2}>
                    <Txt color="gray.500">От данного пользователя поступила<br/>заявка на создание ТТ</Txt>
                  </Box>
                </PopoverContent>
              </Popover>
            </Box>
            <Box display="flex" alignItems="center" gap={2}>
              <Txt color="gray.500">Создал</Txt>
              <Popover
                placement='bottom'
                closeOnBlur={false}
                trigger='hover'
              >
                <PopoverTrigger>
                  <Txt color="brand.500">{rowData.master_user?.full_name || '-'}</Txt>
                </PopoverTrigger>
                <PopoverContent>
                  <Box p={2}>
                    <Txt color="gray.500">Данный пользователь создал ТТ</Txt>
                  </Box>
                </PopoverContent>
              </Popover>
            </Box>
          </Flex>;
        }
      },
    },
    {
      name: 'id',
      label: 'Изм.',
      options: {
        hidden: !canEdit,
        filter: false,
        sort: false,
        customBodyRender: (id, { rowData }) => (
          <IconButton
            onClick={() => history.push(`/acl/outlets/${id}/`, {
              masterOutlet: rowData,
            })} 
            aria-label='edit'            
          >
            <Edit />
          </IconButton>
        ),
      },
    },
    {
      name: 'update_dt',
      label: 'Дата обновления',
      options: {
        filter: {
          name: 'update_date_range',
          type: 'date-range',
          onApply: (value) => value ? [value[0], value[1]] : undefined
        },
        sort: false,
        customBodyRender: dateTimeBodyRender(false, true),
      },
    }
  ];
};
